import React from "react";

/**
 * SkylineOverlay
 *
 * Detailed NYC-inspired skyline anchored to the bottom-right of hero sections.
 * Modeled after a premium real-estate brochure illustration: layered tower
 * silhouettes with subtle window-grid textures, art-deco crowns, a tapered
 * supertall with antenna, Brooklyn Bridge with suspension cables, and the
 * Statue of Liberty on the far right.
 *
 * Designed for navy primary backgrounds. Decorative only.
 */

interface Props {
  className?: string;
  /** Kept for API compatibility. Ignored, opacity tuned internally. */
  opacityClass?: string;
  /** "white" (default, for navy bg) or "blue" */
  tone?: "white" | "blue";
}

const SkylineOverlay: React.FC<Props> = ({ className = "", tone = "white" }) => {
  const base = tone === "white" ? "255,255,255" : "30,58,95";
  const gold = "214,176,98";

  // Generate horizontal window grid lines for a rectangular tower
  const windowLines = (
    x: number,
    y: number,
    w: number,
    h: number,
    rowGap = 6,
    colGap = 6
  ) => {
    const rows = Math.floor(h / rowGap);
    const cols = Math.floor(w / colGap);
    const lines: React.ReactNode[] = [];
    for (let r = 1; r < rows; r++) {
      lines.push(
        <line
          key={`hr-${x}-${y}-${r}`}
          x1={x}
          x2={x + w}
          y1={y + r * rowGap}
          y2={y + r * rowGap}
          stroke={`rgba(${base},0.18)`}
          strokeWidth="0.35"
        />
      );
    }
    for (let c = 1; c < cols; c++) {
      lines.push(
        <line
          key={`vr-${x}-${y}-${c}`}
          x1={x + c * colGap}
          x2={x + c * colGap}
          y1={y}
          y2={y + h}
          stroke={`rgba(${base},0.12)`}
          strokeWidth="0.3"
        />
      );
    }
    return lines;
  };

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      <svg
        className="absolute bottom-0 right-0 h-[78%] w-[62%] max-w-[920px] opacity-[0.28]"
        viewBox="0 0 1000 700"
        fill="none"
        preserveAspectRatio="xMaxYMax meet"
        style={{
          // Only horizontal fade on the LEFT edge so spires/Liberty/bridge
          // remain fully visible at the top and right.
          maskImage:
            "linear-gradient(to right, transparent 0%, black 35%)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent 0%, black 35%)",
        }}
      >
        <defs>
          <linearGradient id="towerFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={`rgba(${base},0.65)`} />
            <stop offset="100%" stopColor={`rgba(${base},0.22)`} />
          </linearGradient>
          <linearGradient id="towerFillMid" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={`rgba(${base},0.5)`} />
            <stop offset="100%" stopColor={`rgba(${base},0.18)`} />
          </linearGradient>
          <linearGradient id="towerFillBack" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={`rgba(${base},0.32)`} />
            <stop offset="100%" stopColor={`rgba(${base},0.10)`} />
          </linearGradient>
          <linearGradient id="waterFade" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={`rgba(${base},0.18)`} />
            <stop offset="100%" stopColor={`rgba(${base},0)`} />
          </linearGradient>
        </defs>

        {/* ===== BACK LAYER, distant city haze ===== */}
        <g fill="url(#towerFillBack)" stroke={`rgba(${base},0.3)`} strokeWidth="0.5">
          <rect x="40" y="430" width="55" height="220" />
          <rect x="100" y="400" width="40" height="250" />
          <rect x="145" y="420" width="62" height="230" />
          <rect x="212" y="380" width="48" height="270" />
          <rect x="265" y="410" width="60" height="240" />
          <rect x="330" y="390" width="44" height="260" />
          <rect x="380" y="420" width="56" height="230" />
          <rect x="442" y="395" width="40" height="255" />
          <rect x="488" y="415" width="58" height="235" />
          <rect x="552" y="385" width="46" height="265" />
          <rect x="604" y="410" width="60" height="240" />
          <rect x="670" y="395" width="42" height="255" />
          <rect x="718" y="420" width="56" height="230" />
        </g>

        {/* ===== MID LAYER ===== */}
        <g>
          {/* Mid-rise cluster left */}
          <g fill="url(#towerFillMid)" stroke={`rgba(${base},0.45)`} strokeWidth="0.55">
            <rect x="80" y="470" width="72" height="180" />
            <rect x="158" y="440" width="60" height="210" />
            <rect x="224" y="465" width="54" height="185" />
          </g>
          {windowLines(80, 470, 72, 180, 8, 7)}
          {windowLines(158, 440, 60, 210, 8, 7)}
          {windowLines(224, 465, 54, 185, 8, 7)}
        </g>

        {/* ===== FRONT LAYER, signature buildings ===== */}

        {/* Chrysler-inspired stepped art-deco tower with spire */}
        <g>
          <path
            d="M295 650 L295 360 L301 350 L320 350 L326 360 L326 318 L332 308 L348 308 L354 318 L354 280 L360 270 L370 270 L376 280 L376 650 Z"
            fill="url(#towerFill)"
            stroke={`rgba(${base},0.6)`}
            strokeWidth="0.7"
          />
          {/* Art-deco crown ribs */}
          {[0, 1, 2, 3, 4].map((i) => (
            <path
              key={`crown-${i}`}
              d={`M339 ${280 + i * 6} Q365 ${275 + i * 6} 391 ${280 + i * 6}`}
              stroke={`rgba(${base},0.55)`}
              strokeWidth="0.4"
              fill="none"
              opacity={0.7 - i * 0.1}
            />
          ))}
          {/* Spire */}
          <line x1="365" y1="270" x2="365" y2="195" stroke={`rgba(${base},0.7)`} strokeWidth="1.1" />
          <line x1="365" y1="195" x2="365" y2="160" stroke={`rgba(${base},0.55)`} strokeWidth="0.7" />
          <circle cx="365" cy="158" r="2.2" fill={`rgba(${gold},0.95)`} />
          {/* Window grid on body */}
          {windowLines(295, 360, 81, 290, 7, 6)}
        </g>

        {/* Empire-state-style stepped block */}
        <g>
          <path
            d="M392 650 L392 380 L402 370 L460 370 L470 380 L470 650 Z"
            fill="url(#towerFill)"
            stroke={`rgba(${base},0.55)`}
            strokeWidth="0.65"
          />
          <rect x="416" y="335" width="30" height="35" fill="url(#towerFill)" stroke={`rgba(${base},0.55)`} strokeWidth="0.6" />
          <line x1="431" y1="335" x2="431" y2="265" stroke={`rgba(${base},0.65)`} strokeWidth="0.9" />
          {windowLines(392, 380, 78, 270, 8, 7)}
        </g>

        {/* ONE WTC, tapered supertall with antenna (CENTERPIECE) */}
        <g>
          <path
            d="M488 650 L506 200 L572 200 L590 650 Z"
            fill="url(#towerFill)"
            stroke={`rgba(${base},0.7)`}
            strokeWidth="0.8"
          />
          {/* Center facet line for the tapered glass effect */}
          <line x1="539" y1="200" x2="539" y2="650" stroke={`rgba(${base},0.45)`} strokeWidth="0.5" />
          {/* Diagonal facet lines */}
          <line x1="506" y1="200" x2="590" y2="650" stroke={`rgba(${base},0.2)`} strokeWidth="0.3" />
          <line x1="572" y1="200" x2="488" y2="650" stroke={`rgba(${base},0.2)`} strokeWidth="0.3" />
          {/* Window rows following taper */}
          {Array.from({ length: 40 }).map((_, i) => {
            const t = i / 40;
            const y = 200 + t * 450;
            const x1 = 506 + (488 - 506) * t;
            const x2 = 572 + (590 - 572) * t;
            return (
              <line
                key={`wtc-${i}`}
                x1={x1}
                x2={x2}
                y1={y}
                y2={y}
                stroke={`rgba(${base},0.16)`}
                strokeWidth="0.3"
              />
            );
          })}
          {/* Antenna */}
          <line x1="539" y1="200" x2="539" y2="90" stroke={`rgba(${base},0.75)`} strokeWidth="1.2" />
          <line x1="539" y1="120" x2="539" y2="95" stroke={`rgba(${base},0.55)`} strokeWidth="0.6" />
          <circle cx="539" cy="88" r="2.4" fill={`rgba(${gold},0.95)`} />
        </g>

        {/* Cluster of glass towers behind/beside WTC */}
        <g>
          <path
            d="M598 650 L598 340 L608 330 L644 330 L654 340 L654 650 Z"
            fill="url(#towerFillMid)"
            stroke={`rgba(${base},0.5)`}
            strokeWidth="0.6"
          />
          <line x1="626" y1="330" x2="626" y2="285" stroke={`rgba(${base},0.55)`} strokeWidth="0.7" />
          {windowLines(598, 340, 56, 310, 7, 7)}

          <path
            d="M660 650 L660 380 L668 370 L702 370 L710 380 L710 650 Z"
            fill="url(#towerFillMid)"
            stroke={`rgba(${base},0.5)`}
            strokeWidth="0.6"
          />
          {windowLines(660, 380, 50, 270, 7, 7)}

          <path
            d="M718 650 L718 310 L726 300 L760 300 L768 310 L768 650 Z"
            fill="url(#towerFill)"
            stroke={`rgba(${base},0.55)`}
            strokeWidth="0.65"
          />
          <line x1="743" y1="300" x2="743" y2="250" stroke={`rgba(${base},0.6)`} strokeWidth="0.8" />
          {windowLines(718, 310, 50, 340, 7, 7)}
        </g>

        {/* 432 Park-style slim supertall */}
        <g>
          <rect
            x="780"
            y="180"
            width="38"
            height="470"
            fill="url(#towerFill)"
            stroke={`rgba(${base},0.6)`}
            strokeWidth="0.65"
          />
          {windowLines(780, 180, 38, 470, 9, 9)}
          <line x1="799" y1="180" x2="799" y2="135" stroke={`rgba(${base},0.6)`} strokeWidth="0.7" />
        </g>

        {/* Right side towers leading to Liberty */}
        <g>
          <path
            d="M828 650 L828 360 L836 350 L868 350 L876 360 L876 650 Z"
            fill="url(#towerFillMid)"
            stroke={`rgba(${base},0.5)`}
            strokeWidth="0.6"
          />
          {windowLines(828, 360, 48, 290, 7, 7)}

          <rect
            x="882"
            y="400"
            width="40"
            height="250"
            fill="url(#towerFillMid)"
            stroke={`rgba(${base},0.5)`}
            strokeWidth="0.55"
          />
          {windowLines(882, 400, 40, 250, 7, 7)}
        </g>

        {/* Statue of Liberty, far right */}
        <g transform="translate(935 425)">
          {/* Star-shaped pedestal base */}
          <path
            d="M-2 220 L42 220 L42 195 L36 195 L36 175 L4 175 L4 195 L-2 195 Z"
            fill="url(#towerFill)"
            stroke={`rgba(${base},0.55)`}
            strokeWidth="0.55"
          />
          {/* Pedestal upper */}
          <rect x="6" y="120" width="28" height="55" fill="url(#towerFill)" stroke={`rgba(${base},0.55)`} strokeWidth="0.55" />
          <rect x="10" y="105" width="20" height="15" fill="url(#towerFill)" stroke={`rgba(${base},0.55)`} strokeWidth="0.5" />
          {/* Robed body */}
          <path d="M14 105 L26 105 L29 65 L11 65 Z" fill={`rgba(${base},0.5)`} stroke={`rgba(${base},0.6)`} strokeWidth="0.55" />
          {/* Drapery folds */}
          <line x1="17" y1="65" x2="15" y2="105" stroke={`rgba(${base},0.4)`} strokeWidth="0.4" />
          <line x1="20" y1="65" x2="20" y2="105" stroke={`rgba(${base},0.4)`} strokeWidth="0.4" />
          <line x1="23" y1="65" x2="25" y2="105" stroke={`rgba(${base},0.4)`} strokeWidth="0.4" />
          {/* Head */}
          <circle cx="20" cy="56" r="5" fill={`rgba(${base},0.55)`} stroke={`rgba(${base},0.65)`} strokeWidth="0.5" />
          {/* Crown spikes */}
          <path
            d="M11 50 L13 42 M15 49 L16 40 M19 48 L19 38 M23 49 L22 40 M28 50 L26 42"
            stroke={`rgba(${base},0.7)`}
            strokeWidth="0.8"
            fill="none"
          />
          {/* Tablet (left arm) */}
          <rect x="6" y="68" width="6" height="10" fill={`rgba(${base},0.45)`} stroke={`rgba(${base},0.55)`} strokeWidth="0.4" />
          {/* Torch arm */}
          <path d="M27 60 L37 35" stroke={`rgba(${base},0.7)`} strokeWidth="1.3" fill="none" />
          {/* Torch flame */}
          <path
            d="M37 35 Q39 28 37 22 Q35 28 37 35 Z"
            fill={`rgba(${gold},0.85)`}
            stroke={`rgba(${gold},1)`}
            strokeWidth="0.5"
          />
          <circle cx="37" cy="33" r="2.5" fill={`rgba(${gold},0.6)`} opacity="0.7" />
        </g>

        {/* Brooklyn-bridge-inspired span across lower right */}
        <g stroke={`rgba(${base},0.5)`} strokeWidth="0.7" fill="none">
          {/* deck */}
          <line x1="600" y1="580" x2="920" y2="580" />
          <line x1="600" y1="585" x2="920" y2="585" strokeWidth="0.4" />
          {/* gothic towers */}
          <path
            d="M672 580 L672 500 L664 500 L664 488 L680 488 L680 500 L672 500 Z M668 510 Q672 502 676 510 M668 524 Q672 516 676 524"
            fill={`rgba(${base},0.25)`}
          />
          <path
            d="M848 580 L848 500 L840 500 L840 488 L856 488 L856 500 L848 500 Z M844 510 Q848 502 852 510 M844 524 Q848 516 852 524"
            fill={`rgba(${base},0.25)`}
          />
          {/* main suspension catenary */}
          <path d="M600 580 Q636 510 672 500 Q760 562 848 500 Q884 510 920 580" />
          <path d="M610 580 Q640 520 672 510 Q760 565 848 510 Q880 520 910 580" opacity="0.55" />
          {/* vertical suspenders */}
          {Array.from({ length: 26 }).map((_, i) => {
            const x = 605 + i * 12;
            const t = (x - 600) / (920 - 600);
            // approximate the catenary sag between the two towers
            let curveY: number;
            if (x < 672) {
              const lt = (x - 600) / (672 - 600);
              curveY = 580 - (1 - lt) * 0 - lt * 80;
            } else if (x > 848) {
              const rt = (x - 848) / (920 - 848);
              curveY = 500 + rt * 80;
            } else {
              const mt = (x - 672) / (848 - 672);
              const sag = Math.sin(mt * Math.PI) * 28;
              curveY = 500 + sag;
            }
            return (
              <line
                key={`susp-${i}`}
                x1={x}
                x2={x}
                y1={curveY}
                y2={580}
                strokeWidth="0.3"
                stroke={`rgba(${base},0.35)`}
              />
            );
          })}
        </g>

        {/* Water reflection / harbor line */}
        <rect x="0" y="650" width="1000" height="50" fill="url(#waterFade)" />
        <line x1="30" y1="650" x2="970" y2="650" stroke={`rgba(${base},0.25)`} strokeWidth="0.5" />
        <line x1="60" y1="660" x2="940" y2="660" stroke={`rgba(${base},0.15)`} strokeWidth="0.4" strokeDasharray="3 7" />
        <line x1="100" y1="672" x2="900" y2="672" stroke={`rgba(${base},0.1)`} strokeWidth="0.35" strokeDasharray="2 9" />
      </svg>
    </div>
  );
};

export default SkylineOverlay;
